import React from 'react';
import { Button, MenuItemProps, Table, useToasts } from 'freemium-ui';
import { History } from 'history';
import { get, pick, uniq } from 'lodash';
import qs from 'query-string';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import CreatedBy from 'routes/components/CreatedBy';
import { GLOBAL_PER_PAGE, SEGMENTS_LIMIT } from 'routes/constants';
import ElementInView from 'shared/components/ElementInView';
import { useAccessControl } from 'utils/access-control';
import {
  AccountInfoType,
  BreadcrumpDataType,
  ConfirmationType,
  ContactFormType,
  FormServFieldType,
  SegmentType,
  SegmentsResponseType,
  UserType,
} from '../../../../shared/types';
import EmptyState from '../../../../shared/components/EmptyState';
import {
  BACK_END_FIELDS,
  COLUMNS_HIDE_ORDERING_KEY,
} from '../../../../shared/constants';
import HeaderComponent from '../../../components/HeaderComponent';
import SegmentContactsCount from '../components/segment-contacts-count';
import { useLocalStorage } from '../../../../hooks';
import {
  useDeleteSegment,
  useGetSegment,
  useGetSegments,
  useGetSegmentsCount,
} from '../hooks';
import { getTableColumnsFromFields } from '../../../utils';
import { TableDataContainer } from '../../../styles';
import { getSegmentsColumnSettings, segmentFields } from '../constants';

type SegmentsProps = {
  contactForm: ContactFormType;
  setBreadcrumpData?: (
    str: BreadcrumpDataType | React.SetStateAction<BreadcrumpDataType>,
  ) => void;
  setConfirmationData: (
    str: ConfirmationType | React.SetStateAction<ConfirmationType>,
  ) => void;
  accountInfo?: AccountInfoType;
  userInfo?: UserType;
  contactsCount: number;
  fetchContactCount: () => void;
};

const Segments = (props: SegmentsProps) => {
  const { t } = useTranslation();
  const {
    push,
    location: { pathname },
  }: History = useHistory();
  const [, setToast] = useToasts();
  const { access } = useAccessControl();
  const {
    contactForm,
    setBreadcrumpData,
    setConfirmationData,
    accountInfo,
    userInfo,
    contactsCount,
    fetchContactCount,
  } = props;

  const fields = React.useMemo(
    () =>
      contactForm?.fields?.filter(
        (contact) => !BACK_END_FIELDS.includes(contact.name),
      ) || [],
    [contactForm],
  );
  const columnSettings = React.useMemo(
    () => getSegmentsColumnSettings({ accountInfo, userInfo }),
    [accountInfo],
  );
  const columns = React.useMemo(
    () =>
      getTableColumnsFromFields({
        fields: segmentFields.map((eSegment) => ({
          ...eSegment,
          label: `${t(eSegment.label || '')}`,
        })),
        columnSettings,
        contactLists: [],
        accountInfo,
        skipFields: ['no_of_contacts', 'creator_id', 'updater_id'],
      }).map((column) => {
        const newColumn = { ...column };
        if (newColumn.id === 'no_of_contacts') {
          newColumn.accessor = (sg: SegmentType) => (
            <ElementInView>
              <SegmentContactsCount id={sg.id || ''} />
            </ElementInView>
          );
        }
        if (['creator_id', 'updater_id'].includes(newColumn.id)) {
          newColumn.accessor = (sg: SegmentType) => (
            <ElementInView>
              <CreatedBy userId={sg[newColumn.id] || ''} />
            </ElementInView>
          );
        }
        return newColumn;
      }),
    [fields, columnSettings, accountInfo],
  );

  const [count, setCount] = React.useState<number>(0);
  const [pageToken, setPageToken] = React.useState<any>({ 0: '' });
  const [pageIndex, setPageIndex] = React.useState<number>(0);
  const [cloneId, setCloneId] = React.useState<string>('');
  const [columnOrder, setColumnOrder] = useLocalStorage(
    'segments-column-order',
    uniq(
      segmentFields.map(
        (field: FormServFieldType) => field[COLUMNS_HIDE_ORDERING_KEY],
      ),
    ),
  );
  const [hiddenColumns, setHiddenColumns] = useLocalStorage(
    'segments-hidden-columns',
    segmentFields
      .filter(
        (field: FormServFieldType) =>
          !(
            columnSettings[field.name]?.showInitial ||
            columnSettings[field.name]?.disableReordering
          ),
      )
      .map((field) => field[COLUMNS_HIDE_ORDERING_KEY]),
  );

  const rowMenuOptions = React.useMemo<MenuItemProps[]>(() => {
    const rowOptions: MenuItemProps[] = [];
    if ((access || []).indexOf('contact:write') > -1) {
      rowOptions.push(
        ...[
          {
            id: 'edit',
            name: `${t('edit')}`,
          },
          {
            id: 'delete',
            name: `${t('delete')}`,
          },
          {
            id: 'duplicate',
            name: `${t('duplicate')}`,
            disabled:
              count >=
              (Number(accountInfo?.resource_limit_configuration?.segments) ||
                SEGMENTS_LIMIT),
          },
        ],
      );
    }
    if ((access || []).indexOf('contact:read') > -1) {
      rowOptions.push({
        id: 'edit',
        name: `${t('view')}`,
      });
    }
    return rowOptions;
  }, [access, count]);

  const { data: segmentsCountData, isFetching: isFetchingSegmentsCount } =
    useGetSegmentsCount({});

  const {
    data: segmentsData,
    isFetching: isFetchingSegments,
    refetch: refetchSegments,
    isError: isSegmentsError,
  } = useGetSegments({
    page_token: pageToken[pageIndex] || '',
    callback: (isSuccess: boolean, response: SegmentsResponseType) => {
      if (isSuccess) {
        const nextURL = get(response, ['links', 'next', 'href'], '');
        if (nextURL) {
          const parseData: any = qs.parseUrl(nextURL);
          if (parseData.query?.next_token) {
            setPageToken((pgTkn) => ({
              ...pgTkn,
              [pageIndex + 1]: parseData.query.next_token,
            }));
          }
        }
      }
    },
  });

  const { data: segmentData, refetch } = useGetSegment({
    segmentId: cloneId,
  });

  const { mutate: deleteSegment } = useDeleteSegment({
    callback: (success: boolean) => {
      if (success) setConfirmationData({});
    },
    setToast,
  });

  const { segment_rules: segments = [] } = segmentsData || {};

  React.useEffect(() => {
    if (!segmentData || (segmentData && !segmentData.id)) return;
    setCloneId('');
    setConfirmationData({});
    push(
      `${pathname}/create?formValues=${JSON.stringify({
        ...pick(segmentData, ['entity', 'name', 'rule', 'segment_type']),
        name: segmentData.name
          ? `${t('copy_of', {
              label: segmentData.name || '',
            })}`
          : '',
      })}`,
    );
  }, [segmentData]);

  React.useEffect(() => {
    refetchSegments();
  }, [pageIndex]);

  React.useEffect(() => {
    const newCount = segmentsCountData?.count
      ? Number(segmentsCountData.count || '0')
      : 0;
    if (setBreadcrumpData)
      setBreadcrumpData({
        path: [],
        count: newCount,
      });
    setCount(newCount);
    if (window && window.heap) {
      window.heap.addUserProperties({
        segments_count: newCount,
      });
    }
  }, [segmentsCountData]);

  return (
    <TableDataContainer className="segments-container">
      <Table
        data={segments || []}
        columns={columns}
        count={count}
        hideTableOnEmpty
        isLoading={!segmentsData && !isSegmentsError}
        loaderStyles={{ minHeight: 'calc(100vh - 136px)' }}
        pageIndex={pageIndex}
        isFetching={isFetchingSegments || isFetchingSegmentsCount}
        pageSize={GLOBAL_PER_PAGE}
        hiddenColumns={hiddenColumns}
        columnOrder={columnOrder}
        disableRowSelect
        hideFilters
        dataKey="id"
        hideBottomPagination
        cellPropGetter={(cell: any) => ({
          className: cell.column.id,
        })}
        onRowClick={(row: any) => {
          push(`/segments/${get(row, ['original', 'id'], '')}/edit`);
        }}
        onMenuOptionSelect={(option: string, rowData: any) => {
          switch (option) {
            case 'edit':
              push(`${pathname}/${rowData.id}/edit`);
              break;
            case 'delete':
              setConfirmationData({
                id: new Date().toISOString(),
                title: `${t('confirm_deletion')}`,
                className: 'error',
                confirmButtonText: `${t('delete')}`,
                onConfirm: () => {
                  setConfirmationData((old) => ({
                    ...old,
                    confirmButtonDisabled: true,
                  }));
                  deleteSegment(rowData);
                },
                data: rowData,
                fill: '#d7461f',
                infoText: `${t('contacts.menu.delete.infotext')}`,
                description: (
                  <Trans
                    i18nKey="contacts.menu.delete.segment_desc"
                    values={{
                      segment: rowData.name,
                    }}
                    components={{ bold: <b /> }}
                  />
                ),
                glyph: 'info',
              });
              break;
            case 'duplicate':
              setCloneId(rowData.id);
              refetch();
              setConfirmationData({
                id: new Date().toISOString(),
                title: `${t('segments.options.duplicate', {
                  field_name: rowData.name,
                })}`,
                className: 'info',
                onDismiss: () => {
                  setCloneId('');
                },
                confirmButtonText: `${t('loading')}`,
                confirmButtonDisabled: true,
                data: rowData,
                fill: '#2C5CC5',
                infoText: `${t('segments.options.infotext')}`,
                description: '',
                glyph: 'info',
              });
              break;
          }
        }}
        onNextPageClick={() => setPageIndex(pageIndex + 1)}
        onPreviousPageClick={() => setPageIndex(pageIndex - 1)}
        enableNextPage={!!segmentsData?.links?.next}
        enablePreviousPage={pageIndex > 0}
        headerComponent={
          <HeaderComponent
            type="segment"
            columnOrder={columnOrder}
            isLoading={isFetchingSegments || isFetchingSegmentsCount}
            hideExportColumns
            columnSettings={columnSettings}
            showNewSegemnt
            segmentsCount={count}
            setConfirmationData={setConfirmationData}
            setColumnOrder={(cols: string[]) => setColumnOrder(cols)}
            hiddenColumns={hiddenColumns}
            setHiddenColumns={(cols: string[]) => setHiddenColumns(cols)}
            fields={fields}
            columns={segmentFields.map((eField) => ({
              ...eField,
              label: `${t(eField.label || '')}`,
            }))}
            accountInfo={accountInfo}
            executeSegment={() => {
              fetchContactCount();
            }}
            contactsCount={contactsCount}
          />
        }
        emptyComponent={
          <EmptyState
            minHeight="calc(100vh - 204px)"
            title={`${t('empty_state.title.no_segments_to_display')}`}
            message={
              isSegmentsError
                ? `${t('empty_state.message.network_error')}`
                : (access || []).indexOf('contact:write') > -1
                ? `${t('empty_state.message.click_to_add_segments')}`
                : undefined
            }
            imgSrc="empty-state-contacts.svg"
          >
            {(access || []).indexOf('contact:write') > -1 && (
              <Button
                id="new-segment-btn"
                inline
                onClick={() => push('/segments/create')}
              >
                {`+ ${t('segments.add_new_segments')}`}
              </Button>
            )}
          </EmptyState>
        }
        rowMenuOptions={() => rowMenuOptions}
      />
    </TableDataContainer>
  );
};

export default Segments;
